"use client";

import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EASE } from "@/lib/animations";
import { ABOUT_TRANSPARENCY_CONTENT } from "@/data/about-transparency";
import { LaurelBadge } from "./LaurelBadge";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export function AboutTransparency() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const statRef = useRef<HTMLSpanElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { background, card } = ABOUT_TRANSPARENCY_CONTENT;

  useLayoutEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      /* ─── Card entrance ─── */
      if (!reduced) {
        gsap.fromTo(
          cardRef.current,
          { opacity: 0, y: 80, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1.1,
            ease: EASE.out,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 70%",
              once: true,
            },
          }
        );

        /* Heading */
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            delay: 0.2,
            ease: EASE.out,
            scrollTrigger: {
              trigger: cardRef.current,
              start: "top 75%",
              once: true,
            },
          }
        );

        /* Badges stagger */
        const badgeItems = badgesRef.current?.querySelectorAll("[data-badge]");
        if (badgeItems?.length) {
          gsap.fromTo(
            badgeItems,
            { opacity: 0, scale: 0.6, rotate: -10 },
            {
              opacity: 1,
              scale: 1,
              rotate: 0,
              duration: 0.7,
              stagger: 0.12,
              ease: "back.out(1.4)",
              scrollTrigger: {
                trigger: badgesRef.current,
                start: "top 80%",
                once: true,
              },
            }
          );
        }

        /* Quote */
        gsap.fromTo(
          quoteRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            delay: 0.3,
            ease: EASE.out,
            scrollTrigger: {
              trigger: quoteRef.current,
              start: "top 85%",
              once: true,
            },
          }
        );
      }

      /* ─── Big number counter ─── */
      if (statRef.current) {
        const target = card.stat.value;
        const obj = { val: reduced ? target : 0 };

        gsap.to(obj, {
          val: target,
          duration: reduced ? 0 : 2.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: statRef.current,
            start: "top 85%",
            once: true,
          },
          onUpdate: () => {
            if (statRef.current) {
              statRef.current.textContent = Math.round(obj.val).toString();
            }
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [card.stat.value]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      aria-labelledby="transparency-heading"
    >
      {/* ─── Fixed parallax background image ─── */}
      <div
        className="relative min-h-[720px] sm:min-h-[780px] lg:min-h-[820px] w-full bg-cover bg-center"
        style={{
          backgroundImage: `url(${background.src})`,
          backgroundAttachment: "fixed", // creates the "fixed" parallax feel
        }}
        role="img"
        aria-label={background.alt}
      >
        {/* Warm tonal overlay to blend image with the yellow card */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 via-transparent to-amber-950/20 pointer-events-none" />

        {/* ─── Overlapping Yellow Card ─── */}
        <div className="absolute inset-x-0 bottom-0 flex justify-center lg:justify-end px-5 sm:px-8 lg:px-16 pb-0 lg:pb-0">
          <div
            ref={cardRef}
            className="relative w-full max-w-[900px] rounded-t-[32px] lg:rounded-[32px] lg:mb-[-60px] shadow-2xl shadow-black/20 will-change-transform"
            style={{
              backgroundColor: "#d4e816",
            }}
          >
            <div className="grid gap-10 p-8 sm:p-12 lg:p-14 lg:grid-cols-[1.2fr_1fr] lg:gap-8">
              {/* ─── LEFT SIDE ─── */}
              <div className="flex flex-col justify-between gap-10">
                {/* Badges Row */}
                <div ref={badgesRef} className="flex items-center gap-3">
                  <div data-badge>
                    <LaurelBadge
                      label={card.badges[0].label}
                      sublabel={card.badges[0].sublabel}
                      variant="laurel"
                    />
                  </div>
                  <div data-badge>
                    <LaurelBadge
                      label={card.badges[1].label}
                      sublabel={card.badges[1].sublabel}
                      variant="star"
                    />
                  </div>
                  <div data-badge>
                    <LaurelBadge
                      label={card.badges[2].label}
                      sublabel={card.badges[2].sublabel}
                      variant="shield"
                    />
                  </div>
                </div>

                {/* Testimonial */}
                <div ref={quoteRef}>
                  <p className="text-[15px] sm:text-[16px] leading-[1.65] text-stone-900 max-w-[420px]">
                    <span className="mr-1 text-[18px] leading-none align-top">
                      &ldquo;
                    </span>
                    {card.testimonial.quote}
                    <span className="ml-1 text-[18px] leading-none align-top">
                      &rdquo;
                    </span>
                  </p>

                  <div className="mt-5">
                    <p
                      className="text-[24px] text-stone-900 leading-none"
                      style={{
                        fontFamily:
                          '"Great Vibes", "Allura", "Dancing Script", cursive',
                      }}
                    >
                      {card.testimonial.authorName}
                    </p>
                    <p className="mt-2 text-[13px] text-stone-800">
                      {card.testimonial.authorRole}
                    </p>
                  </div>
                </div>
              </div>

              {/* ─── DIVIDER ─── */}
              <div className="hidden lg:flex flex-col">
                <div className="flex h-full">
                  <div className="w-px h-full bg-stone-900/25" />
                  <div className="flex flex-col justify-between pl-8 flex-1">
                    {/* Heading */}
                    <h2
                      ref={headingRef}
                      id="transparency-heading"
                      className="text-[clamp(1.8rem,3vw,2.6rem)] font-bold leading-[1.1] tracking-tight text-stone-900"
                    >
                      {card.heading}
                    </h2>

                    {/* Big Number */}
                    <div className="flex flex-col">
                      <div className="flex items-baseline">
                        <span
                          ref={statRef}
                          className="text-[clamp(5rem,9vw,8.5rem)] font-black leading-[0.9] tracking-[-0.05em] text-stone-900 tabular-nums"
                        >
                          0
                        </span>
                        <span className="text-[clamp(5rem,9vw,8.5rem)] font-black leading-[0.9] tracking-[-0.05em] text-stone-900">
                          {card.stat.suffix}
                        </span>
                      </div>
                      <p className="mt-2 text-[15px] font-medium text-stone-800">
                        {card.stat.label}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── MOBILE FALLBACK for right column ─── */}
              <div className="lg:hidden flex flex-col gap-8 pt-8 border-t border-stone-900/20">
                <h2 className="text-[clamp(1.8rem,6vw,2.4rem)] font-bold leading-[1.1] tracking-tight text-stone-900">
                  {card.heading}
                </h2>

                <div>
                  <div className="flex items-baseline">
                    <span className="text-[clamp(4rem,15vw,6rem)] font-black leading-[0.9] tracking-[-0.05em] text-stone-900 tabular-nums">
                      {card.stat.value}
                    </span>
                    <span className="text-[clamp(4rem,15vw,6rem)] font-black leading-[0.9] tracking-[-0.05em] text-stone-900">
                      {card.stat.suffix}
                    </span>
                  </div>
                  <p className="mt-2 text-[14px] font-medium text-stone-800">
                    {card.stat.label}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom spacer so overlapping card breathes into the next section */}
      <div className="hidden lg:block h-[60px] bg-white" />
    </section>
  );
}